(() => {
  if (customElements.get('cart-cross-sell')) return;

  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.onCartUpdated = (event) => {
        if (Array.isArray(event.detail?.cart?.items)) {
          this.update(event.detail.cart.items.map((item) => item.product_id));
        }
      };
      this.onCartRefresh = () => this.refreshCart();
      document.addEventListener('cart:updated', this.onCartUpdated);
      document.addEventListener('cart:refresh', this.onCartRefresh);
      queueMicrotask(() => {
        if (!this.isConnected) return;
        try { this.update(JSON.parse(this.dataset.productIds || '[]')); }
        catch { this.hidden = true; }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.cartController?.abort();
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('cart:refresh', this.onCartRefresh);
    }

    async refreshCart() {
      this.cartController?.abort();
      const controller = new AbortController();
      this.cartController = controller;
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const response = await fetch(this.dataset.cartUrl, { signal: controller.signal });
        if (!response.ok) return;
        const cart = await response.json();
        if (!controller.signal.aborted && this.isConnected && this.cartController === controller && Array.isArray(cart.items)) this.update(cart.items.map((item) => item.product_id));
      } catch {} finally { clearTimeout(timeout); }
    }

    async candidates(productId, intent, signal) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('section_id', 'cart-cross-sell');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      try {
        const response = await fetch(url, { signal });
        if (!response.ok) return [];
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(html.querySelectorAll('[data-cross-sell-product]'));
      } catch { return []; }
    }

    select(candidates, cartIds, limit) {
      const seen = new Set(cartIds);
      return candidates.filter((card) => {
        const id = card.dataset.crossSellProduct;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      }).slice(0, limit);
    }

    async update(productIds) {
      const ids = [...new Set(productIds.map(String))];
      const key = ids.join(',');
      if (this.cartKey === key) return;
      this.cartKey = key;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const list = this.querySelector('[data-cross-sell-list]');
      if (!list) return;
      this.hidden = true;
      list.replaceChildren();
      if (!ids.length) return;
      const limit = Math.min(3, Math.max(1, Number(this.dataset.limit) || 3));
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const seeds = ids.slice(0, 3);
        const complementary = (await Promise.all(seeds.map((id) => this.candidates(id, 'complementary', controller.signal)))).flat();
        let selected = this.select(complementary, ids, limit);
        if (selected.length < limit && !controller.signal.aborted) {
          const related = (await Promise.all(seeds.map((id) => this.candidates(id, 'related', controller.signal)))).flat();
          selected = this.select([...complementary, ...related], ids, limit);
        }
        if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
        selected.forEach((card) => {
          const node = document.importNode(card, true);
          node.querySelectorAll('form[id]').forEach((form) => { form.id += `-${this.dataset.instance}`; });
          list.appendChild(node);
        });
        this.hidden = selected.length === 0;
      } finally { clearTimeout(timeout); }
    }
  }

  customElements.define('cart-cross-sell', CartCrossSell);
})();
